import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthMethodType, SelectData } from '../../models/global.model';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, of } from 'rxjs';
import { ApiConfiguration as ApiConfigurationMsp } from 'src/app/msp-api/api-configuration';
import { ApiConfiguration } from 'src/app/api/api-configuration';
import { AuthenticationApiService, UserHelpApiService, UserProfileApiService, UserTowerGroupsApiService } from 'src/app/api/services';
import { AppConfig } from 'src/app/config/routes.config';
import { AuthService } from 'src/app/core/auth/auth.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { environment } from 'src/environments/environment';
import { AuthTypes, StorageEnums } from 'src/shared/models/enums';
import { ICustomControl } from 'src/shared/models/form.model';
import { CustomSpinnerService } from './custom-spinner.service';
import { AlertController, ModalController } from '@ionic/angular/standalone';
import { TowerGroupDto } from 'src/app/api/models';
import { ContextHelpListComponent } from 'src/shared/components/context-help/context-help-list/context-help-list.component';
import { BookingHelperService } from './booking-helper.service';
import { FacilityListWrapperComponent } from 'src/app/modules/home-wrapper/my-subscription/components/facility-list-wrapper/facility-list-wrapper.component';

@Injectable({
	providedIn: 'root'
})
export class HelperService {
	public countries: Array<SelectData>;
	public phoneCodeCountries: Array<SelectData>;
	private routes = AppConfig.routes;
	contextHelpModal: HTMLIonModalElement;

	constructor(private http: HttpClient,
				private router: Router,
				private AuthenticationApiService: AuthenticationApiService,
				private translateService: TranslateService,
				private storage: StorageService,
				private alertCtrl: AlertController,
				private towerApiService: UserTowerGroupsApiService,
				public authService: AuthService,
				private profileUserService: UserProfileApiService,
				private modalCtrl: ModalController,
				private contextHelpService: UserHelpApiService,
				private bookingHelper: BookingHelperService,
				private apiConfiguration: ApiConfiguration,
				private apiConfigurationMsp: ApiConfigurationMsp,
				private spinner: CustomSpinnerService
	) {

	}

	getLanguages() {
		return [
			{
				name: 'English',
				value: 'en'
			},
			{
				name: 'Deutsch',
				value: 'de'
			}

		] as Array<SelectData>;
	}


	getGenders() {
		console.log(this.translateService.getDefaultLang());
		return [
			{
				value: 'FEMALE',
				name: this.translateService.instant('users.form.female')
			},
			{
				value: 'MALE',
				name: this.translateService.instant('users.form.male')
			},
			{
				value: 'UNKNOWN',
				name: this.translateService.instant('users.form.unknown')
			},

		] as Array<SelectData>;
	}

	getDeliveryPoints() {
		return {
			0: this.translateService.instant("delivery-points.no-delivery"),
			1: this.translateService.instant("delivery-points.customer-provide-one"),
			2: this.translateService.instant("delivery-points.customer-provide-multiple"),
			3: this.translateService.instant("delivery-points.you-provide-one"),
			4: this.translateService.instant("delivery-points.you-provide-multiple")
		};
	}

	getDeleteUserReasons() {
		return [
			{ id: 0, label: 'delete-reasons.0' },
			{ id: 1, label: 'delete-reasons.1' },
			{ id: 2, label: 'delete-reasons.2' },
			{ id: 3, label: 'delete-reasons.3' },
			{ id: 4, label: 'delete-reasons.4' }
		];
	}

	getCountries() {
		this.http.get('assets/static/countries.json').subscribe((r: Array<any>) => {
			this.countries = r.map(c => {
				return {
					name: c.name,
					value: c.code,
					disable: c.disable,
					callingCode: c.callingCode
				};
			});

			this.getCountriesForPhoneSelect();
		});
	}

	private getCountriesForPhoneSelect() {
		this.phoneCodeCountries = [];
		this.countries.forEach(c => this.phoneCodeCountries.push(Object.create(c)));

		this.phoneCodeCountries.forEach(c => {
			c.name = `${ c.name }(${ c.callingCode })`;
			this.phoneCodeCountries.push();
		});
	}

	async checkRoute(key: string, showContactUs: boolean = false, group: TowerGroupDto = null) {
		const language = (await this.storage.getItem(StorageEnums.LANG))?.toLocaleUpperCase() || 'EN';
		this.contextHelpService.getHelpPagebyRoute({key, language, web: true}).subscribe(async (p) => {
		  this.showContextHelp(showContactUs, true, p, language, group);
		}, async (err) => {
		  this.showContextHelp(showContactUs, false, null, language);
		});
	}

	async showContextHelp(showContactUs = false, openDetails = false, helpPage = null, language: string, group?: TowerGroupDto) {
		this.contextHelpModal = await this.modalCtrl.create({
		  id: 'booking-details',
		  component: ContextHelpListComponent,
		  cssClass: 'auto-height',
		  componentProps: {
			openDetails,
			helpPage,
			showContactUs: showContactUs,
			group: group,
			language: language
		  }
		});
		this.contextHelpModal.onDidDismiss().then(async v => {
		});
		this.contextHelpModal.present();
	}

	async closeAllModals() {
		if(this.contextHelpModal) await this.contextHelpModal.dismiss();
		await this.bookingHelper.closeContactUs();
	}

	getCountryByCode(code) {
		return this.countries.find(c => c.value === code);
	}

	getAuthMethodStyle(type: string) {
		const style: AuthMethodType = {
			clazz: '',
			icon: '',
			name: ''
		};
		style.icon = type;
		switch (type) {
			case 'swisspass':
				style.clazz = 'button-outline red';
				style.name = 'SwissPass';
				style.iconWidth = 28;
				break;
			case 'google':
				style.clazz = 'button-outline black';
				style.name = 'Google';
				style.iconWidth = 28;
				break;
			case 'facebook':
				style.clazz = '';
				style.name = 'Facebook';
				style.iconWidth = 28;
				break;
			case 'apple':
				style.clazz = 'black';
				style.name = 'Apple';
				style.iconWidth = 28;
				break;
			default:
				style.clazz = 'red email button-outline';
				style.icon = 'email-colored';
				style.iconWidth = 28;
				style.name = this.translateService.instant('auth.login.choose-sign-in-method.email');
				break;
		}
		return style;
	}

	getStatusIconName(g: TowerGroupDto) {
		let name;
		switch (g.status) {
			case 'AVAILABLE':
				name = 'tower-status-green';
				break;
			case 'OCCUPIED':
				name = 'tower-status-red';
				break;
			case 'OUT_OF_ORDER':
				name = 'tower-status-black';
				break;
		}

		return name;
	}
	
	getIncludedFieldsForProfile(type: 'profile' | 'personal-info' | 'profile-address' | 'sign-up') {
		let list: Array<string>;

		switch (type) {
			case 'personal-info':
				list = [
					'id',
					'firstName',
					'lastName',
					'email',
					'phoneCountryCode',
					'phoneMobile',
					'gender',
					'createdDate',
					'language'
				];
				break;
			case 'sign-up':
				list = [
					'id',
					'firstName',
					'lastName',
					'email',
					'phoneCountryCode',
					'phoneMobile'
				];
				break;
			case 'profile-address':
				list = [
					'id',
					'address',
					'address2',
					'city',
					'postCode',
					'countryCode',
					'createdDate',
				];
				break;
			case 'profile':
				list = [
					'id',
					'firstName',
					'lastName',
					'email',
					'phoneMobile',
					'gender',
					'address',
					'address2',
					'city',
					'postCode',
					'countryCode',
					'createdDate',
					'language'
				];
				break;
		}
		return list;
	}


	async logout(sendRequest = true, needRedirect = false) {
		this.authService.updateAuth(false);
		let authTOken = await this.storage.getItem(StorageEnums.AUTH_TOKEN);
		await this.storage.setItem(StorageEnums.ApiUrl, (!document.location.origin.includes('localhost') ? document.location.origin: environment.api));
		this.modalCtrl.getTop().then(async t => {
			if (t) {
				await t.dismiss();
			}
		});
		if (sendRequest) {
			if (authTOken) {
				this.AuthenticationApiService.logout1().subscribe(async r => {
					await this.spinner.hide();
					await this.storage.clearOnLogout();
					try {	
						const profile = await firstValueFrom(this.profileUserService.getUserProfile());
						if(profile?.account?.provider === 'swisspass') {
							this.swissPassLogout();
						}
					} catch (error) { }
					setTimeout(async () => {
						await this.router.navigate([this.routes.auth.path]);
					}, 100);
				});
			}
		} else {
			await this.storage.clearOnLogout();
			await this.spinner.hide();
			if (needRedirect) {
				setTimeout(async () => {
					await this.router.navigate([this.routes.auth.path]);
				}, 100);
			}
		}
	}

	updateElementByClick(id, focus = false) {
		let elementById = document.getElementById(id);
		setTimeout(() => {
			if (elementById) {
				elementById.click();
			}

			if (focus) {
				elementById.focus();
			}
		}, 100);
	}

	extractedPhone(code, phone) {
		let c = this.countries.find(c => c.value === code);
		let callingCode
		if (c) {
			callingCode = c.callingCode;
		} else {
			callingCode = '';
		}
		return phone.replace(callingCode, '')
	}

	setPhoneWithoutCode(user) {
		let { phoneCountryCode, phoneMobile } = user;
		let res;
		let c = this.countries.find(c => c.value === phoneCountryCode);
		if (c) {
			res = `${ c.callingCode }${ phoneMobile }`;
		} else {
			res = phoneMobile;
		}
		user.phoneMobile = res;
		return res;
	}

	getPhoneWithoutCode(user) {
		let { phoneCountryCode, phoneMobile } = user;
		let phone;
		let c = this.countries.find(c => c.value === phoneCountryCode);
		if (c) {
			phone = `${ c.callingCode }${ phoneMobile }`;
		} else {
			phone = phoneMobile;
		}
		return phone;
	}

	filterUserControls(user: Array<ICustomControl>, type: 'profile' | 'personal-info' | 'profile-address') {
		return user.filter(u => u.id === this.getIncludedFieldsForProfile(type).find(c => c === u.id));
	}

	resetForm(form: FormGroup, controls: ICustomControl[]) {
		let reset = {};
		controls.forEach(c => {
			reset[c.id] = '';
		})
		form.reset(reset);
	}

	setCssVariable(clazz, propertyName, propertyValue) {
		const el: any = document.querySelector('.' + clazz);
		if (el) {
			el.style.setProperty('--' + propertyName, propertyValue);
		}
	}


	isAppVersionInvalid(backVersion, appVersion, withRedirect = true) {
		// console.log('isAppVersionInvalid', appVersion, backVersion,);
		// let appVersionSplit = appVersion.split('.');
		// let backVersionSplit = backVersion.split('.');
		// this.storage.setItem(StorageEnums.MIN_SERVER_API_VERSION, backVersion);
		// let isInvalid = false;
		// appVersionSplit.forEach((v, index) => {
		// 	let appVersion = Number(appVersionSplit[index]);
		// 	let serverMinVersion = Number(backVersionSplit[index]);
		//
		// 	if ((appVersion < serverMinVersion && withRedirect)) {
		// 		// console.log('isInvalid', appVersion, serverMinVersion);
		// 		isInvalid = true;
		// 		this.router.navigate(['/app-version']);
		// 	}
		// });

		// return isInvalid;
		return false;
	}

	async checkIsStageUser(email = null) {
		let emails = await this.storage.getItem(StorageEnums.EmailsForFilter);
		let userEmail = email || (await this.storage.getItem(StorageEnums.USER_EMAIL));
		console.log('checkIsStageUser', email);
		console.log('emails', emails);
		let found = emails?.find(user => user.email === userEmail);
		if (found && document.location.origin.includes('live')) {
			console.log('found', found);
			this.apiConfiguration.rootUrl = environment.testApi;
			this.apiConfigurationMsp.rootUrl = environment.testApi;
			await this.storage.setItem(StorageEnums.IsStage, true);
		} else {
			await this.storage.removeItem(StorageEnums.IsStage);
		}

		return of([1]);
	}

	async oauth(onSuccess, network: AuthTypes, type: 'sign-in' | 'sign-up' | 'change') {
		const origin = !document.location.origin.includes('localhost') ? document.location.origin: environment.api;
		let returnUrl = origin + environment.oauthLocalReturnUrl;
		let urlPath = '';
		switch (type) {
			case 'sign-in':
				urlPath = 'auth/sign-in/choose-method';
				break;
			case 'sign-up':
				urlPath = 'auth/sign-up/manually';
				break;
			case 'change':
				urlPath = 'settings';
				break;
		}
		returnUrl = `${ origin + environment.oauthLocalReturnUrl }${ urlPath }`;
		let oauthUrl = `${origin + environment.oauthUrl }${ network }?redirect_uri=${ returnUrl }`;

		const mainAction = async () => {
			if (type === 'change') {
				const token = await this.storage.getItem(StorageEnums.AUTH_TOKEN);
				oauthUrl = `${ oauthUrl }&token=${ token }`;
			}
			window.open(oauthUrl, '_self');
		}
		await mainAction();
	}

	async openFacilitiesList(currentStep: number = 8) {
		try {
			await this.modalCtrl.dismiss();
		} catch (error) {}
		const groups = await firstValueFrom(this.towerApiService.getUserTowerGroups());
		const modal = await this.modalCtrl.create({
            id: 'booking-details',
            component: FacilityListWrapperComponent,
            cssClass: ['auto-height'],
            componentProps: {
                groups,
				currentStep,
            }
        });

        modal.onWillDismiss().then(() => {
        });

        await modal.present();
	}

	async locationPermission() {
		const alert = await this.alertCtrl.create({
			cssClass: 'forgot-password-alert',
			header: this.translateService.instant('alerts.title'),
			message: this.translateService.instant('permissions.location-not-found'),
			mode: 'ios',
			buttons: [{
				text: this.translateService.instant('buttons.ok')
			}]
		});
		await alert.present();
	}

	getSwissPassLogoutUrl() {
		if(this.apiConfiguration.rootUrl.includes('test') || this.apiConfiguration.rootUrl.includes('localhost')) {
			return 'https://login-test.swisspass.ch/v3/oevlogin/logout';
		}
		else if(this.apiConfiguration.rootUrl.includes('stage')) {
			return 'https://login-int.swisspass.ch/v3/oevlogin/logout';
		}
		else if(this.apiConfiguration.rootUrl.includes('live')) {
			return 'https://login.swisspass.ch/v3/oevlogin/logout';
		}
	}

	swissPassLogout() {
		const url = this.getSwissPassLogoutUrl();
		if(url) {
			setTimeout(() => {
				this.http.get(url, { withCredentials: true })
					.subscribe(response => {
				});
			}, 100);
		}
	}
}
